import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { isAdminUser } from '@/utils/role';
import {
  HomeIcon,
  ShoppingBagIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  ShoppingBagIcon as ShoppingBagIconSolid,
  UserIcon as UserIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
  ShoppingCartIcon as ShoppingCartIconSolid,
} from '@heroicons/react/24/solid';

interface NavItem {
  path: string;
  label: string;
  iconOutline: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
  ariaLabel: string;
}

export const DashboardBottomBar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [, setIsMobile] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  const safeParseUser = (value: string | null) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  // CRÉER LE PORTAL TOUJOURS (même si pas d'utilisateur) - CRITIQUE
  // Ce useEffect doit s'exécuter IMMÉDIATEMENT au montage du composant
  // IMPORTANT: Ce useEffect s'exécute même si le composant retourne null plus tard
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const createPortalContainer = () => {
      let portalContainer = document.getElementById('bottom-navbar-portal');
      
      if (portalContainer) {
        // Vérifier qu'il est bien dans le body
        if (portalContainer.parentNode !== document.body) {
          document.body.appendChild(portalContainer);
        }
        setPortalReady(true);
        return;
      }
      
      portalContainer = document.createElement('div');
      portalContainer.id = 'bottom-navbar-portal';
      
      // Styles critiques pour isoler complètement le portal
      portalContainer.style.cssText = `
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 999999 !important;
        pointer-events: none !important;
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        isolation: isolate !important;
        transform: none !important;
        will-change: auto !important;
        perspective: none !important;
        filter: none !important;
        contain: none !important;
      `;
      
      // S'assurer que le portal est ajouté au body
      if (document.body) {
        document.body.appendChild(portalContainer);
        setPortalReady(true);
      } else {
        // Si body n'existe pas encore, attendre
        let attempts = 0;
        const waitForBody = setInterval(() => {
          attempts++;
          if (document.body) {
            document.body.appendChild(portalContainer);
            setPortalReady(true);
            clearInterval(waitForBody);
          } else if (attempts > 50) {
            clearInterval(waitForBody);
          }
        }, 10);
      }
    };

    // Essayer immédiatement
    createPortalContainer();
    
    // Essayer aussi après plusieurs délais pour être sûr
    const timeoutId1 = setTimeout(createPortalContainer, 50);
    const timeoutId2 = setTimeout(createPortalContainer, 100);
    const timeoutId3 = setTimeout(createPortalContainer, 200);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, []); // S'exécute une seule fois au montage

  // Vérifier si on est sur mobile
  useEffect(() => {
    setMounted(true);
    const checkMobile = () => {
      const width = window.innerWidth;
      const isMobileWidth = width < 1024;
      setIsMobile(isMobileWidth);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    const timeoutId = setTimeout(checkMobile, 100);
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  // Déterminer si l'utilisateur est admin (via roleName, pas un ID fixe)
  // Utiliser l'utilisateur du contexte ou du storage
  const userFromStorage = typeof window !== 'undefined'
    ? safeParseUser(sessionStorage.getItem('user'))
    : null;
  const currentUser = user || userFromStorage;
  const isAdmin = isAdminUser(currentUser);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Menu pour ADMIN
  const adminMenuItems: NavItem[] = [
    {
      path: '/admin/dashboard',
      label: t('bottomBar.dashboard'),
      iconOutline: Squares2X2Icon,
      iconSolid: Squares2X2IconSolid,
      ariaLabel: t('bottomBar.dashboard'),
    },
    {
      path: '/admin/commandes',
      label: t('bottomBar.orders'),
      iconOutline: ClipboardDocumentListIcon,
      iconSolid: ClipboardDocumentListIconSolid,
      ariaLabel: t('bottomBar.ariaManageOrders'),
    },
    {
      path: '/admin/liste-produits',
      label: t('bottomBar.products'),
      iconOutline: ShoppingBagIcon,
      iconSolid: ShoppingBagIconSolid,
      ariaLabel: t('bottomBar.products'),
    },
    {
      path: '/boutique',
      label: t('bottomBar.home'),
      iconOutline: HomeIcon,
      iconSolid: HomeIconSolid,
      ariaLabel: t('bottomBar.ariaPublicAccess'),
    },
  ];

  // Menu pour CLIENT
  const clientMenuItems: NavItem[] = [
    {
      path: '/mes-commandes',
      label: t('bottomBar.orders'),
      iconOutline: ClipboardDocumentListIcon,
      iconSolid: ClipboardDocumentListIconSolid,
      ariaLabel: t('bottomBar.ariaMyOrders'),
    },
    {
      path: '/profil',
      label: t('bottomBar.profile'),
      iconOutline: UserIcon,
      iconSolid: UserIconSolid,
      ariaLabel: t('bottomBar.profile'),
    },
    {
      path: '/panier',
      label: t('bottomBar.cart'),
      iconOutline: ShoppingCartIcon,
      iconSolid: ShoppingCartIconSolid,
      ariaLabel: t('bottomBar.cart'),
    },
    {
      path: '/boutique',
      label: t('bottomBar.home'),
      iconOutline: HomeIcon,
      iconSolid: HomeIconSolid,
      ariaLabel: t('bottomBar.ariaPublicAccess'),
    },
  ];

  // Sélectionner le menu selon le rôle
  // Si on est sur une page admin, utiliser le menu admin par défaut
  const isAdminPage = location.pathname.startsWith('/admin');
  const menuItems = (isAdmin || isAdminPage) ? adminMenuItems : clientMenuItems;

  // Attendre que le composant soit monté et que le portal soit prêt
  if (!mounted || !portalReady) {
    return null;
  }

  // Vérifier la largeur de l'écran directement
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const shouldShow = screenWidth < 1024;
  
  if (!shouldShow) {
    return null;
  }

  // Si on est sur une page admin, afficher la navbar même si l'utilisateur n'est pas encore chargé
  // (l'utilisateur sera chargé par le contexte d'authentification)
  if (!currentUser && !isAdminPage) {
    return null;
  }

  const navContent = (
    <nav 
      className="dashboard-bottom-bar"
      id="dashboard-bottom-navbar"
      style={{ 
        // Position fixe absolue par rapport au viewport
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        maxWidth: '100vw',
        height: 'var(--bottom-nav-height-safe, 64px)',
        minHeight: 'var(--bottom-nav-height, 64px)',
        backgroundColor: 'var(--color-white, white)',
        borderTop: '1px solid var(--color-medium-grey, #e5e7eb)',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '0 16px',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        margin: 0,
        isolation: 'isolate',
        pointerEvents: 'auto',
        visibility: 'visible',
        opacity: 1,
        transform: 'none',
        willChange: 'auto',
        boxSizing: 'border-box',
      }}
      role="navigation"
      aria-label={t('bottomBar.ariaMainNav')}
    >
      {menuItems.map((item) => {
        const IconOutline = item.iconOutline;
        const IconSolid = item.iconSolid;
        const active = isActive(item.path);
        const Icon = active ? IconSolid : IconOutline;
        
        return (
          <Link
            key={item.path}
            to={item.path}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              color: active ? 'var(--color-cranberry)' : 'var(--color-text-body, #6b7280)',
              textDecoration: 'none',
              flex: 1,
              minWidth: 0,
              transition: 'color 0.2s',
              fontSize: '12px',
              fontWeight: active ? '600' : '400',
            }}
            aria-label={item.ariaLabel}
          >
            <Icon className="dashboard-bottom-bar-icon" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  // Utiliser un portal pour rendre directement dans le body
  // Cela isole la navbar de tous les parents avec transform/overflow
  if (typeof document === 'undefined') {
    return null;
  }

  // Récupérer le conteneur portal (créé dans le useEffect précédent)
  let portalContainer = document.getElementById('bottom-navbar-portal');
  
  // Si le portal n'existe pas encore, le créer immédiatement (fallback)
  if (!portalContainer && document.body) {
    portalContainer = document.createElement('div');
    portalContainer.id = 'bottom-navbar-portal';
    portalContainer.style.cssText = `
      position: fixed !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      z-index: 999999 !important;
      pointer-events: none !important;
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      isolation: isolate !important;
      transform: none !important;
      will-change: auto !important;
      perspective: none !important;
      filter: none !important;
      contain: none !important;
    `;
    document.body.appendChild(portalContainer);
  }
  
  if (!portalContainer) {
    return null;
  }

  return createPortal(navContent, portalContainer);
};
